import type { FlagCategory } from '@generated/graphql';
import {
  FLAG_CATEGORY_TO_I18N,
  getReconFlagTypeLabel,
} from '@ovComponents/4-module/reconFlags/utils/reconFlags.config';
import type { TFunction } from 'i18next';
import type {
  FlagGlossaryEntry,
  FlagGlossaryRowView,
} from './flagGlossary.types';
import { formatGlossaryThreshold } from './formatGlossaryThreshold';

export const GLOSSARY_EMPTY_FIELD = '—';

const getGlossaryI18nField = (
  t: TFunction,
  flagType: string,
  field: 'description' | 'exampleTrigger',
): string => {
  const value = t(`flagGlossary.entries.${flagType}.${field}`, {
    defaultValue: GLOSSARY_EMPTY_FIELD,
  });
  return value === `flagGlossary.entries.${flagType}.${field}`
    ? GLOSSARY_EMPTY_FIELD
    : value;
};

export const getFlagGlossaryCategoryLabel = (
  t: TFunction,
  category: FlagCategory,
): string => {
  const i18nKey = FLAG_CATEGORY_TO_I18N[category];
  return t(`reconFlags.categories.${i18nKey}`, { defaultValue: category });
};

export const getFlagGlossaryPriorityLabel = (
  t: TFunction,
  priority: string,
): string =>
  t(`reconFlags.severityLabel.${priority}`, { defaultValue: priority });

export const mapFlagGlossaryRow = (
  entry: FlagGlossaryEntry,
  t: TFunction,
): FlagGlossaryRowView => ({
  entry,
  flagName: entry.label || getReconFlagTypeLabel(t, entry.flagType),
  description: getGlossaryI18nField(t, entry.flagType, 'description'),
  exampleTrigger: getGlossaryI18nField(t, entry.flagType, 'exampleTrigger'),
  categoryLabel: getFlagGlossaryCategoryLabel(t, entry.category),
  actualPriorityLabel: getFlagGlossaryPriorityLabel(t, entry.actual.priority),
  actualThresholdDisplay: formatGlossaryThreshold(
    entry.actual,
    entry.axis,
    entry.isAmountThresholdable,
  ),
  defaultPriorityLabel: getFlagGlossaryPriorityLabel(
    t,
    entry.inherited.priority,
  ),
  defaultThresholdDisplay: formatGlossaryThreshold(
    entry.inherited,
    entry.axis,
    entry.isAmountThresholdable,
  ),
});

export const mapFlagGlossaryRows = (
  entries: FlagGlossaryEntry[],
  t: TFunction,
): FlagGlossaryRowView[] =>
  entries.map((entry) => mapFlagGlossaryRow(entry, t));
